import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, ArrowRight, Crown, Send, Users, Workflow } from "lucide-react";
import api from "@/lib/api";
import { fmtDateTime, inr } from "@/lib/format";

const SIGNAL_LABELS = { like: "Liked", dislike: "Disliked", wishlist: "Wishlisted", view: "Viewed" };

export default function CrmDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/crm/overview").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);

  if (!data) return <p className="text-sm font-mono text-neutral-400">LOADING…</p>;

  const stats = [
    { label: "Customers", value: data.total_customers ?? 0, icon: Users, testid: "crm-stat-customers" },
    { label: "Lifetime value", value: inr(data.total_ltv || 0), icon: Crown, testid: "crm-stat-ltv" },
    { label: "Active journeys", value: data.active_journeys ?? 0, icon: Workflow, testid: "crm-stat-journeys" },
    { label: "Runs in progress", value: data.active_runs ?? 0, icon: Activity, testid: "crm-stat-runs" },
    { label: "Sends (7d)", value: data.sends_7d ?? 0, icon: Send, testid: "crm-stat-sends" },
  ];

  return (
    <div className="space-y-6" data-testid="crm-dashboard">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold tracking-tight">CRM</h1>
        <div className="flex gap-2">
          <Link to="/admin/crm/customers" className="text-xs font-mono uppercase tracking-widest border border-neutral-300 px-3 py-2 hover:border-neutral-900 transition-colors" data-testid="crm-view-customers">
            Customers 360
          </Link>
          <Link to="/admin/crm/journeys" className="text-xs font-mono uppercase tracking-widest border border-neutral-300 px-3 py-2 hover:border-neutral-900 transition-colors" data-testid="crm-view-journeys">
            Journeys
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {stats.map(({ label, value, icon: Icon, testid }) => (
          <div key={label} className="bg-white border border-neutral-200 p-4" data-testid={testid}>
            <div className="flex items-center justify-between text-neutral-400">
              <span className="text-[10px] font-mono uppercase tracking-widest">{label}</span>
              <Icon size={15} />
            </div>
            <p className="font-heading text-xl font-bold mt-2">{value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-neutral-200 p-4">
        <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-3">Segments</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(data.segments || {}).map(([key, s]) => (
            <Link key={key} to={`/admin/crm/customers?segment=${key}`}
              className="flex items-center gap-2 border border-neutral-200 px-3 py-1.5 text-xs hover:border-brand transition-colors"
              data-testid={`crm-segment-${key}`}>
              {s.label} <span className="font-mono font-semibold text-brand">{s.count}</span>
            </Link>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-neutral-200">
          <div className="p-4 border-b border-neutral-100 flex items-center justify-between">
            <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Recent journey sends (mock adapters)</p>
            <Link to="/admin/crm/journeys" className="text-xs text-brand flex items-center gap-1" data-testid="crm-sends-more">All <ArrowRight size={12} /></Link>
          </div>
          <div className="divide-y divide-neutral-100">
            {(data.recent_sends || []).map((s) => (
              <div key={s.id} className="p-3 text-xs" data-testid={`crm-send-${s.id}`}>
                <div className="flex justify-between gap-2">
                  <span className="font-mono uppercase">{s.channel}</span>
                  <span className={`font-mono ${s.status === "mock_sent" ? "text-emerald-600" : "text-amber-600"}`}>{s.status}</span>
                </div>
                <p className="text-neutral-500 mt-1 truncate">{s.to} — {s.body}</p>
                <p className="text-neutral-400 mt-0.5">{fmtDateTime(s.created_at)}</p>
              </div>
            ))}
            {!(data.recent_sends || []).length && <p className="p-4 text-xs text-neutral-400" data-testid="crm-no-sends">No sends yet — create a journey and run the engine.</p>}
          </div>
        </div>

        <div className="bg-white border border-neutral-200">
          <div className="p-4 border-b border-neutral-100">
            <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Recent affinity signals</p>
          </div>
          <div className="divide-y divide-neutral-100">
            {(data.recent_affinity || []).map((a) => (
              <div key={a.id} className="p-3 text-xs flex justify-between gap-2" data-testid={`crm-affinity-${a.id}`}>
                <span><span className="font-medium">{SIGNAL_LABELS[a.signal] || a.signal}</span> {a.title}</span>
                <span className="text-neutral-400 shrink-0">{a.customer_email || a.anon_id}</span>
              </div>
            ))}
            {!(data.recent_affinity || []).length && <p className="p-4 text-xs text-neutral-400" data-testid="crm-no-affinity">No affinity signals yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
